import { faDogLeashed } from '@fortawesome/pro-light-svg-icons';
import {
    faCalendarDay,
    faCalendarDays,
    faCalendarWeek,
    faHotel,
    faPlus,
    faScissors,
    faTennisBall,
    faWhistle
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretLeft, faCaretRight, faLocationDot, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MarketplaceScheduleType } from '@spike/marketplace-model';
import { PageResult, Period } from '@spike/model';
import { Room } from '@spike/room-model';
import DayPicker from 'components/Calendar3/CalendarHeader/DayPicker/DayPicker';
import MonthPicker from 'components/Calendar3/CalendarHeader/MonthPicker/MonthPicker';
import WeekPicker from 'components/Calendar3/CalendarHeader/WeekPicker/WeekPicker';
import { CalendarView, UNASSIGNED_STAFF_ID } from 'components/Calendar3/types';
import Button from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownFooter } from 'components/UI/V3/Dropdown/Footer/DropdownFooter';
import { DropdownGroup } from 'components/UI/V3/Dropdown/Group/DropdownGroup';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { Switch } from 'components/UI/V3/Forms/Switch';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import useServiceCategories from 'hooks/useServiceCategories';
import { BusinessAreaType, SERVICES_BUSINESS_AREAS } from 'model/Service';
import { OrganizationStaff } from 'model/Staff';
import { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from 'store';
import styles from './CalendarHeader.module.scss';

interface CalendarHeaderProps {
    businessArea: BusinessAreaType;
    onChangeBusinessArea: (businessArea: BusinessAreaType) => void;
    timeZone: string;
    //
    viewMode: CalendarView;
    onChangeViewMode: (viewMode: CalendarView) => void;
    //
    date: Moment;
    onChangeDate: (date: Moment) => void;
    week: Period;
    onChangeWeek: (week: Period) => void;
    month: Moment;
    onChangeMonth: (date: Moment) => void;
    //
    isSlotsView: boolean;
    onChangeSlotsView: (value: boolean) => void;
    //
    selectedStaffIds: Array<number> | undefined;
    onChangeStaffIds?: (selectedStaffIds: Array<number> | undefined) => void;
    //
    selectedLocationId: number;
    onChangeLocationId?: (selectedLocationId: number) => void;
    //
    selectedRoomUnitIds: Array<number> | undefined;
    onChangeRoomUnitIds?: (selectedRoomUnitIds: Array<number> | undefined) => void;
    //
    readOnly?: boolean;
    //
    onAdd?: () => void;
    onToday: () => void;
}

const SERVICE_CATEGORY_MAP = [
    {
        id: SERVICES_BUSINESS_AREAS.GROOMING,
        label: 'Grooming',
        icon: faScissors
    },
    {
        id: SERVICES_BUSINESS_AREAS.DAYCARE,
        label: 'Daycare',
        icon: faTennisBall
    },
    {
        id: SERVICES_BUSINESS_AREAS.TRAINING,
        label: 'Training',
        icon: faWhistle
    },
    {
        id: SERVICES_BUSINESS_AREAS.WALKING,
        label: 'Walking',
        icon: faDogLeashed
    },
    {
        id: SERVICES_BUSINESS_AREAS.BOARDING,
        label: 'Boarding',
        icon: faHotel
    }
];

const CALENDAR_VIEW_MODE_MAP = [
    {
        id: CalendarView.Daily,
        label: 'Day',
        icon: faCalendarDay,
        disabled: (businessArea: BusinessAreaType) =>
            businessArea === SERVICES_BUSINESS_AREAS.BOARDING || businessArea === SERVICES_BUSINESS_AREAS.DAYCARE
    },
    {
        id: CalendarView.Weekly,
        label: 'Week',
        icon: faCalendarWeek,
        disabled: (businessArea: BusinessAreaType) => businessArea === SERVICES_BUSINESS_AREAS.DAYCARE
    },
    {
        id: CalendarView.Monthly,
        label: 'Month',
        icon: faCalendarDays,
        disabled: (businessArea: BusinessAreaType) => businessArea === SERVICES_BUSINESS_AREAS.BOARDING
    }
];

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
    timeZone,
    viewMode,
    onChangeViewMode,
    businessArea,
    onChangeBusinessArea,
    isSlotsView,
    onChangeSlotsView,
    date,
    onChangeDate,
    week,
    onChangeWeek,
    month,
    onChangeMonth,
    selectedStaffIds,
    onChangeStaffIds,
    selectedLocationId,
    onChangeLocationId,
    selectedRoomUnitIds,
    onChangeRoomUnitIds,
    readOnly,
    onAdd,
    onToday
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const { hasGrooming, hasDental, hasDaycare, hasBoarding, hasWalking, hasTraining } = useServiceCategories();

    const {
        organizationMarketplaces: locations,
        getOrganizationMarketplace,
        hasBoarding: hasLocationBoarding
    } = useOrganizationMarketplaces();

    const marketplace = getOrganizationMarketplace(selectedLocationId);

    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const organizationStaff = useSelector<RootState, Array<OrganizationStaff>>(state => state.staff.organizationStaff);

    const [isDatePickerOpen, setIsDatePickerOpen] = React.useState<boolean>(false);

    const [isStaffSelectorOpen, setIsStaffSelectorOpen] = React.useState<boolean>(false);
    const [selectedStaffIdsState, setSelectedStaffIdsState] = React.useState<Array<number> | undefined>(
        selectedStaffIds
    );

    const [isLocationSelectorOpen, setIsLocationSelectorOpen] = React.useState<boolean>(false);
    const [selectedLocationState, setSelectedLocationState] = React.useState<{
        selectedId: number;
        newSelectedId: number | undefined;
    }>({ selectedId: selectedLocationId, newSelectedId: selectedLocationId });

    const [isUnitSelectorOpen, setIsUnitSelectorOpen] = React.useState<boolean>(false);
    const [selectedRoomUnitIdsState, setSelectedRoomUnitIdsState] = React.useState<Array<number> | undefined>(
        selectedRoomUnitIds
    );

    const staffs = React.useMemo(() => {
        return organizationStaff
            .filter(
                staff => !staff.deleted && staff.worksAt.some(w => w.marketplaceId === selectedLocationId && w.active)
            )
            .sort((staff, otherStaff) =>
                `${staff.person.firstName} ${staff.person.lastName}`
                    .toLowerCase()
                    .localeCompare(`${otherStaff.person.firstName} ${otherStaff.person.lastName}`.toLocaleLowerCase())
            );
    }, [organizationStaff, selectedLocationId]);

    const staffOptions = React.useMemo(() => {
        const options = [
            { id: UNASSIGNED_STAFF_ID, label: 'Unassigned' },
            ...staffs.map(staff => ({
                id: staff.id!,
                label: `${staff.person.firstName} ${staff.person.lastName}`
            }))
        ];

        return options.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    }, [staffs]);

    const allStaffIds = React.useMemo(() => staffOptions.map(option => option.id), [staffOptions]);

    const selectedViewMode = React.useMemo(() => {
        return CALENDAR_VIEW_MODE_MAP.find(item => item.id === viewMode);
    }, [viewMode]);

    const selectedBusinessArea = React.useMemo(() => {
        return SERVICE_CATEGORY_MAP.find(item => item.id === businessArea);
    }, [businessArea]);

    const serviceCategory = React.useMemo(() => {
        return SERVICE_CATEGORY_MAP.filter(item => {
            if (item.id === SERVICES_BUSINESS_AREAS.GROOMING) {
                return hasGrooming || hasDental;
            }

            if (item.id === SERVICES_BUSINESS_AREAS.DAYCARE) {
                return hasDaycare;
            }

            if (item.id === SERVICES_BUSINESS_AREAS.BOARDING) {
                return hasBoarding && hasLocationBoarding(selectedLocationState.selectedId);
            }

            if (item.id === SERVICES_BUSINESS_AREAS.WALKING) {
                return hasWalking;
            }

            if (item.id === SERVICES_BUSINESS_AREAS.TRAINING) {
                return hasTraining;
            }

            return true;
        });
    }, [hasGrooming, hasDental, hasDaycare, hasBoarding, hasWalking, hasTraining, selectedLocationState.selectedId]);

    const showSlotsSwitch = React.useMemo(() => {
        return (
            businessArea === SERVICES_BUSINESS_AREAS.GROOMING &&
            marketplace?.scheduleType === MarketplaceScheduleType.MultiSlots &&
            [CalendarView.Weekly, CalendarView.Daily].includes(viewMode)
        );
    }, [businessArea, marketplace?.scheduleType, viewMode]);

    const dateLabel = React.useMemo(() => {
        if (viewMode === CalendarView.Weekly) {
            if (!week.from.isSame(week.to, 'month')) {
                return `${week.from.format('MMM DD')} - ${week.to.format('MMM DD, YYYY')}`;
            }

            return `${week.from.format('MMM DD')} - ${week.to.format('DD, YYYY')}`;
        }

        if (viewMode === CalendarView.Monthly) {
            return `${month.format('MMMM, YYYY')}`;
        }

        return `${date.format('MMM')} ${date.format('D')}, ${date.format('ddd')}, ${date.format('YYYY')}`;
    }, [date, week, month, viewMode]);

    const changeBusinessAreaHandler = React.useCallback(
        (businessArea: BusinessAreaType) => {
            if (businessArea === SERVICES_BUSINESS_AREAS.BOARDING) {
                onChangeViewMode(CalendarView.Weekly);
            }

            onChangeBusinessArea(businessArea);
        },
        [onChangeBusinessArea, onChangeViewMode]
    );

    const prevHandler = React.useCallback(() => {
        if (viewMode === CalendarView.Weekly) {
            onChangeWeek({
                from: week.from.clone().subtract(7, 'days'),
                to: week.from.clone().subtract(1, 'day')
            });
        }

        if (viewMode === CalendarView.Daily) {
            onChangeDate(date.clone().subtract(1, 'day'));
        }

        if (viewMode === CalendarView.Monthly) {
            onChangeMonth(month.clone().subtract(1, 'month').startOf('month'));
        }
    }, [viewMode, date, week, month]);

    const nextHandler = React.useCallback(() => {
        if (viewMode === CalendarView.Weekly) {
            onChangeWeek({
                from: week.to.clone().add(1, 'day'),
                to: week.to.clone().add(7, 'days')
            });
        }

        if (viewMode === CalendarView.Daily) {
            onChangeDate(date.clone().add(1, 'day'));
        }

        if (viewMode === CalendarView.Monthly) {
            onChangeMonth(month.clone().add(1, 'month').startOf('month'));
        }
    }, [viewMode, date, week, month]);

    const staffLabel = React.useMemo(() => {
        if (selectedStaffIds === undefined || selectedStaffIds.length === allStaffIds.length) {
            return 'All Staff';
        }

        if (selectedStaffIds.length === 1 && selectedStaffIds[0] === UNASSIGNED_STAFF_ID) {
            return 'Unassigned';
        }

        return `Staff (${selectedStaffIds.length})`;
    }, [selectedStaffIds, allStaffIds.length]);

    const locationLabel = React.useMemo(() => {
        return locations.find(l => l.id === selectedLocationId)?.name ?? 'Not Selected';
    }, [selectedLocationId]);

    const toggleAllStaffHandler = React.useCallback(() => {
        if (selectedStaffIdsState?.length === 0) {
            setSelectedStaffIdsState(allStaffIds);
        } else {
            setSelectedStaffIdsState([]);
        }
    }, [selectedStaffIdsState, allStaffIds]);

    const selectStaffHandler = React.useCallback(
        (staffId: number) => {
            // If undefined, it means all staff are selected
            if (selectedStaffIdsState === undefined) {
                // Select all except the clicked one
                setSelectedStaffIdsState(allStaffIds.filter(id => id !== staffId));
            } else {
                const isSelected = selectedStaffIdsState?.includes(staffId);

                if (isSelected) {
                    setSelectedStaffIdsState(prev => prev?.filter(id => id !== staffId));
                } else {
                    setSelectedStaffIdsState(prev => [...prev!, staffId]);
                }
            }
        },
        [selectedStaffIdsState, allStaffIds]
    );

    const applyStaffHandler = React.useCallback(() => {
        onChangeStaffIds?.(selectedStaffIdsState);
        setIsStaffSelectorOpen(false);
    }, [selectedStaffIdsState]);

    const applyLocationHandler = React.useCallback(() => {
        const nextSelectedId = selectedLocationState.newSelectedId;
        if (nextSelectedId && selectedLocationState.selectedId !== nextSelectedId) {
            setSelectedLocationState(prev => ({ ...prev, selectedId: nextSelectedId }));
            onChangeLocationId?.(nextSelectedId);
        } else {
            setSelectedLocationState(prev => ({ ...prev, newSelectedId: prev.selectedId }));
        }
        setIsLocationSelectorOpen(false);
    }, [selectedLocationState]);

    const roomUnitLabel = React.useMemo(() => {
        if (
            selectedRoomUnitIds === undefined ||
            selectedRoomUnitIds.length === rooms.page.flatMap(room => room.units.map(unit => unit.id!)).length
        ) {
            return 'All Facilities';
        }

        return `Facilities (${selectedRoomUnitIds.length})`;
    }, [selectedRoomUnitIds, rooms]);

    const selectRoomUnitHandler = React.useCallback(
        (roomUnitId: number) => {
            // If undefined, it means all units are selected
            if (selectedRoomUnitIdsState === undefined) {
                // Select all except the clicked one
                const allRoomUnitIds = rooms.page.flatMap(room => room.units.map(unit => unit.id!));
                setSelectedRoomUnitIdsState(allRoomUnitIds.filter(id => id !== roomUnitId));
            } else {
                const isSelected = selectedRoomUnitIdsState?.includes(roomUnitId);

                if (isSelected) {
                    setSelectedRoomUnitIdsState(prev => prev?.filter(id => id !== roomUnitId));
                } else {
                    setSelectedRoomUnitIdsState(prev => [...prev!, roomUnitId]);
                }
            }
        },
        [selectedRoomUnitIdsState, selectedRoomUnitIds, rooms]
    );

    const applyRoomUnitHandler = React.useCallback(() => {
        onChangeRoomUnitIds?.(selectedRoomUnitIdsState);
        setIsUnitSelectorOpen(false);
    }, [selectedRoomUnitIdsState]);

    React.useEffect(() => {
        if (!isStaffSelectorOpen) {
            setSelectedStaffIdsState(selectedStaffIds ?? undefined);
        }
    }, [isStaffSelectorOpen, selectedStaffIds]);

    React.useEffect(() => {
        if (!isUnitSelectorOpen) {
            setSelectedRoomUnitIdsState(selectedRoomUnitIds ?? undefined);
        }
    }, [isUnitSelectorOpen, selectedRoomUnitIds]);

    return (
        <div className={styles.root}>
            <div className={styles.leftGroup}>
                <Button
                    label="Today"
                    variant="outlined"
                    onClick={onToday}
                />

                <div className={styles.dateControls}>
                    <button
                        type="button"
                        className={styles.controlButton}
                        onClick={prevHandler}
                    >
                        <FontAwesomeIcon icon={faCaretLeft} />
                    </button>

                    <Dropdown
                        open={isDatePickerOpen}
                        onOpenChange={setIsDatePickerOpen}
                    >
                        <DropdownTrigger>
                            <span className={styles.dateLabel}>{dateLabel}</span>
                        </DropdownTrigger>
                        <DropdownContent
                            align="start"
                            className={styles.datePickerDropdown}
                        >
                            {viewMode === CalendarView.Daily && (
                                <DayPicker
                                    timeZone={timeZone}
                                    date={date}
                                    onChange={value => {
                                        onChangeDate(value);
                                        setIsDatePickerOpen(false);
                                    }}
                                />
                            )}

                            {viewMode === CalendarView.Weekly && (
                                <WeekPicker
                                    timeZone={timeZone}
                                    week={week}
                                    onChange={value => {
                                        onChangeWeek(value);
                                        setIsDatePickerOpen(false);
                                    }}
                                />
                            )}

                            {viewMode === CalendarView.Monthly && (
                                <MonthPicker
                                    timeZone={timeZone}
                                    month={month}
                                    onChange={value => {
                                        onChangeMonth(value);
                                        setIsDatePickerOpen(false);
                                    }}
                                />
                            )}
                        </DropdownContent>
                    </Dropdown>

                    <button
                        type="button"
                        className={styles.controlButton}
                        onClick={nextHandler}
                    >
                        <FontAwesomeIcon icon={faCaretRight} />
                    </button>
                </div>
            </div>

            <div className={styles.businessAreaSelector}>
                <Dropdown>
                    <DropdownTrigger>
                        <Button
                            variant="outlined"
                            label={selectedBusinessArea?.label}
                            leftIcon={isMobile ? undefined : selectedBusinessArea?.icon}
                        />
                    </DropdownTrigger>

                    <DropdownContent>
                        {serviceCategory.map(item => (
                            <DropdownItem
                                key={item.id}
                                checked={businessArea === item.id}
                                leftIcon={isMobile ? undefined : item.icon}
                                onSelect={() => changeBusinessAreaHandler(item.id)}
                            >
                                {item.label}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            </div>

            <div className={styles.locationSelector}>
                <Dropdown
                    open={isLocationSelectorOpen}
                    onOpenChange={setIsLocationSelectorOpen}
                >
                    <DropdownTrigger className={styles.staffSelector}>
                        <Button
                            label={locationLabel}
                            variant="outlined"
                            leftIcon={faLocationDot}
                        />
                    </DropdownTrigger>

                    <DropdownContent maxHeight={300}>
                        {locations
                            .filter(
                                location =>
                                    businessArea !== SERVICES_BUSINESS_AREAS.BOARDING ||
                                    hasLocationBoarding(location.id)
                            )
                            .map(location => (
                                <DropdownItem
                                    key={location.id}
                                    type="checkbox"
                                    checked={selectedLocationState.newSelectedId === location.id}
                                    onSelect={selected =>
                                        setSelectedLocationState(prev => ({
                                            ...prev,
                                            newSelectedId: selected ? location.id : undefined
                                        }))
                                    }
                                >
                                    {location.name}
                                </DropdownItem>
                            ))}
                        <DropdownFooter>
                            <Button
                                label="Apply"
                                variant="green"
                                onClick={applyLocationHandler}
                                disabled={selectedLocationState.newSelectedId === undefined}
                            />
                        </DropdownFooter>
                    </DropdownContent>
                </Dropdown>
            </div>

            <div className={styles.rightGroup}>
                {showSlotsSwitch && (
                    <Switch
                        inlineLabel="Slots View"
                        checked={isSlotsView}
                        onChange={onChangeSlotsView}
                        className={styles.slotsViewSwitch}
                    />
                )}

                {selectedBusinessArea?.id !== SERVICES_BUSINESS_AREAS.BOARDING && (
                    <Dropdown
                        open={isStaffSelectorOpen}
                        onOpenChange={setIsStaffSelectorOpen}
                    >
                        <DropdownTrigger className={styles.staffSelector}>
                            <Button
                                label={staffLabel}
                                variant="outlined"
                                leftIcon={faUsers}
                            />
                        </DropdownTrigger>

                        <DropdownContent maxHeight={300}>
                            <DropdownItem
                                type="checkbox"
                                checked={
                                    selectedStaffIdsState === undefined ||
                                    selectedStaffIdsState?.length === allStaffIds.length
                                }
                                onSelect={toggleAllStaffHandler}
                            >
                                All Staff
                            </DropdownItem>

                            <hr />

                            {staffOptions.map(option => (
                                <DropdownItem
                                    key={option.id}
                                    type="checkbox"
                                    checked={
                                        selectedStaffIdsState === undefined ||
                                        selectedStaffIdsState?.includes(option.id)
                                    }
                                    onSelect={() => selectStaffHandler(option.id)}
                                >
                                    {option.label}
                                </DropdownItem>
                            ))}

                            <DropdownFooter>
                                <Button
                                    label="Apply"
                                    variant="green"
                                    onClick={applyStaffHandler}
                                    disabled={selectedStaffIdsState?.length === 0}
                                />
                            </DropdownFooter>
                        </DropdownContent>
                    </Dropdown>
                )}

                {selectedBusinessArea?.id === SERVICES_BUSINESS_AREAS.BOARDING && (
                    <Dropdown
                        open={isUnitSelectorOpen}
                        onOpenChange={setIsUnitSelectorOpen}
                    >
                        <DropdownTrigger className={styles.staffSelector}>
                            <Button
                                label={roomUnitLabel}
                                variant="outlined"
                            />
                        </DropdownTrigger>

                        <DropdownContent
                            maxWidth={200}
                            maxHeight={300}
                        >
                            {rooms.page.map(room => (
                                <DropdownGroup
                                    key={room.id}
                                    title={room.name}
                                >
                                    {room.units.map(unit => (
                                        <DropdownItem
                                            key={unit.id}
                                            type="checkbox"
                                            checked={
                                                selectedRoomUnitIdsState === undefined ||
                                                selectedRoomUnitIdsState?.includes(unit.id!)
                                            }
                                            onSelect={() => selectRoomUnitHandler(unit.id!)}
                                        >
                                            {unit.name}
                                        </DropdownItem>
                                    ))}
                                </DropdownGroup>
                            ))}

                            <DropdownFooter>
                                <Button
                                    label="Apply"
                                    variant="green"
                                    onClick={applyRoomUnitHandler}
                                    disabled={selectedRoomUnitIdsState?.length === 0}
                                />
                            </DropdownFooter>
                        </DropdownContent>
                    </Dropdown>
                )}

                <Dropdown>
                    <DropdownTrigger className={styles.viewModeSelector}>
                        <Button
                            variant="outlined"
                            label={selectedViewMode?.label}
                            leftIcon={selectedViewMode?.icon}
                        />
                    </DropdownTrigger>

                    <DropdownContent>
                        {CALENDAR_VIEW_MODE_MAP.map(item => (
                            <DropdownItem
                                key={item.id}
                                leftIcon={isMobile ? undefined : item.icon}
                                checked={viewMode === item.id}
                                onSelect={() => onChangeViewMode(item.id)}
                                disabled={item.disabled?.(businessArea)}
                            >
                                {item.label}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>

                {!readOnly && (
                    <Button
                        label="Add"
                        variant="primary"
                        leftIcon={faPlus}
                        onClick={onAdd}
                        className={styles.addButton}
                    />
                )}
            </div>
        </div>
    );
};
